import type { MsgKey } from "./i18n";
import { TILE_NAV, type Tile } from "./tiles.ts";
import { ACT_ITEM_TILE } from "../../../src/game/act-item-tile.ts";
/**
 * 左侧菜单结构。来源：需求脑图 v2（uploads/mindmap-v2-outline.md）。
 *
 * `done` 标记这一页是否已经实现——未实现的在菜单上打灰点、点进去显示该页将要配的字段。
 * 这比藏起来强：策划能看到整体范围，也知道自己等的那页排在哪。
 */
export interface NavItem {
  id: string;
  /** true = 只有超级管理员可见。菜单与页面都按它过滤；服务端另有一道 */
  superAdmin?: boolean;
  /** true = 只有根超管（环境变量）可见。用于「超管管理」这类提权页 */
  rootOnly?: boolean;
  /** 三级子项。有 children 的项自己也是一页（总览），点标题进总览、点箭头展开 */
  children?: NavItem[];
  /** 兜底文案；界面实际显示走 i18n 的 nav.<id> */
  label: string;
  done?: boolean;
  /** 该页是否受配置版本管辖（有草稿/发布/回滚） */
  versioned?: boolean;
  /**
   * 该页对应的规则文档（页头「?」入口，新窗口打开）。
   * 集中登记在这里而不是各页面组件——文档搬家只改一处。子项未指定时继承父项。
   */
  docUrl?: string;
  /** 未实现时展示的字段清单（i18n key） */
  todo?: MsgKey[];
  /**
   * 项级玩法体系过滤，与 NavGroup.mode 同义但只管这一项。
   * 用在「组两种体系都有、其中一页只属于多人」的情形：棋盘配置组 solo / match 都显示，
   * 但棋盘格位表只有多人匹配才有（单人棋盘在客户端，服务端不知道棋盘）。
   */
  mode?: "solo" | "match";
  /**
   * true = 暂时不进菜单（左栏与搜索都不出），页面与接口照留，直接链接、发布闸的「去修」跳转照常能进。
   * 用于做好了但还没有玩法在用的页，免得运营以为要配。玩法接上后删掉这一格即可恢复
   */
  offMenu?: boolean;
}

export interface NavGroup {
  id: string;
  label: string;
  /**
   * 这一组只属于某一套建筑体系：`levels` 关卡制 / `pettown` 萌宠小镇。
   * 不填 = 两套都用得上。
   */
  system?: "levels" | "pettown";
  /**
   * 这一组只属于某个玩法体系：`solo` 单人主线 / `match` 多人匹配。
   * 不填 = 都用得上（全局参数、玩家管理、报表）。
   *
   * 棋盘、卡牌、idol、拼图、喵汪、活动这些主线内容对多人匹配的代理没有意义，挂着只会
   * 让运营以为自己漏配了。**建筑那两组不在此列**：它们跟着「建筑体系」走——运营在
   * 初始化页选了哪一套，就该看得见那一套的页，哪怕这个代理跑的是多人。
   */
  mode?: "solo" | "match";
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    id: "global",
    label: "全局配置",
    items: [
      // 玩法体系与建筑体系都在这一页选：两个都是开服前必须定、定了决定下面哪些组出现的东西，
      // 分成两页的话运营要在两个地方各选一次，还会出现「这页说关卡制、那页说萌宠小镇」的自相矛盾
      { id: "init-config", label: "初始化配置", done: true, versioned: true, docUrl: "https://portwind.jp.larksuite.com/wiki/ZlmVwskCfiFEuok7ocCjMG6Lphe" },
      { id: "global-dice", label: "骰子参数", done: true, versioned: true, docUrl: "https://portwind.jp.larksuite.com/wiki/T1JqwJZVYieoLxkUaV0jkIkkp0e" },
      { id: "global-fight", label: "对抗与控骰", done: true, versioned: true, docUrl: "https://portwind.jp.larksuite.com/wiki/T1JqwJZVYieoLxkUaV0jkIkkp0e" },
      /**
       * 分享链接：分享页的「社交媒体 / 创作者中心 / Facebook」外链（John 2026-10-05）。
       * 放这一组是因为它与玩法体系无关（solo / match 的代理都有分享页）；
       * 活表、保存即生效、不进配置版本（同系统邮件），所以不带 versioned。
       */
      { id: "share-links", label: "分享链接", done: true },
    ],
  },
  {
    id: "clear",
    label: "通关配置",
    system: "levels",
    items: [
      { id: "cost", label: "总造价配置", done: true, versioned: true },
      { id: "level", label: "关卡配置", done: true, versioned: true },
      { id: "building", label: "建筑配置", done: true, versioned: true },
      { id: "reward", label: "奖励配置", done: true, versioned: true },
      // 天赋树（关卡制）：每升满一栋建筑天赋等级 +1，节点按等级解锁。与萌宠小镇的「天赋树」共用 TalentPage（system="levels"）
      { id: "level-talent", label: "天赋树", done: true, versioned: true },
      // 挂机收益：骰子动画期间点屏幕收金币（2026-10-27）。两个数存在 common，页上带逐关预览
      { id: "idle-income", label: "挂机收益", done: true, versioned: true },
    ],
  },
  {
    /*
     * 萌宠小镇：与「通关配置」互斥——同一个代理只跑一套体系，菜单也按体系切。
     *
     * 原来是两组都留着、由页面自己说「当前体系不是它」。切了 test 的 a0=12 之后
     * 当场看出来不对：明明是萌宠小镇，左边还挂着关卡配置、建筑配置、总造价，
     * 运营根本分不清哪一组是活的。看得见另一套的诉求由「初始化配置」那一页满足——
     * 两套都画在那儿，选哪套写得清清楚楚。
     */
    id: "pettown",
    label: "萌宠小镇",
    system: "pettown",
    items: [
      { id: "pettown-params", label: "小镇参数", done: true, versioned: true, docUrl: "https://portwind.jp.larksuite.com/wiki/Ic1iwhJCzi076YkEiNJj0QahpUd" },
      { id: "pettown-star", label: "主建筑星级表", done: true, versioned: true, docUrl: "https://portwind.jp.larksuite.com/wiki/Ic1iwhJCzi076YkEiNJj0QahpUd" },
      { id: "pettown-slot", label: "副建筑槽位", done: true, versioned: true, docUrl: "https://portwind.jp.larksuite.com/wiki/Ic1iwhJCzi076YkEiNJj0QahpUd" },
      // 营地天赋树（《Fast Ludo 营地天赋树 V3.3》）：按累计星数解锁的纯升级树
      { id: "pettown-talent", label: "天赋树", done: true, versioned: true },
    ],
  },
  {
    id: "match",
    label: "多人对战",
    /*
     * 只属于多人匹配。运营在初始化页选了「多人匹配」之后，这一组才出现——
     * 匹配参数、门票分池、战阶、道具卡都在这里；单人主线的代理看不到，也不该配。
     * 方案：https://portwind.jp.larksuite.com/wiki/Qc2kw7RpCia0PskxXrzjpiDjpBh
     */
    mode: "match",
    items: [
      { id: "match-mode", label: "匹配与房间", done: true, versioned: true, docUrl: "https://portwind.jp.larksuite.com/wiki/ZlmVwskCfiFEuok7ocCjMG6Lphe" },
      { id: "battle-stage", label: "战阶", done: true, versioned: true, docUrl: "https://portwind.jp.larksuite.com/wiki/Qc2kw7RpCia0PskxXrzjpiDjpBh" },
      { id: "ability-card", label: "道具卡", done: true, versioned: true, docUrl: "https://portwind.jp.larksuite.com/wiki/Qc2kw7RpCia0PskxXrzjpiDjpBh" },
      // 游戏中心（2026-11-04）：好友房 / VIP 房的开关与人数候选。不走配置版本（cfg_game_center 一个租户一行，保存即生效）
      { id: "game-center", label: "游戏中心", done: true },
    ],
  },
  {
    id: "board",
    label: "棋盘配置",
    mode: "solo",
    items: [
      { id: "spin", label: "spin 奖励配置", done: true, versioned: true, docUrl: "https://portwind.jp.larksuite.com/wiki/AqXwwQ3HNiBbQkkzU8AjI1nLpCh" },
      {
        id: "subgame",
        label: "副玩法配置",
        done: true,
        docUrl: "https://portwind.jp.larksuite.com/wiki/AqXwwQ3HNiBbQkkzU8AjI1nLpCh",
        // 玩法格随版本快照走（cfg_tile 逐格 PUT 带 CAS），总览和每个格子页都要
        // 进版本横幅集合——漏了就是 spin 页踩过的那个死胡同：已发布版本上
        // 一排灰输入框，没人告诉策划为什么不能编、该去哪建草稿
        versioned: true,
        // 玩法格各一页：字段差异大（承载范围 vs 出现节奏、各自的演出参数），
        // 挤进一张表要么列爆炸、要么半数列对当前行不适用
        children: [
          ...TILE_NAV.map((x) => ({ id: x.id, label: x.label, done: true, versioned: true })),
          /*
           * 活动道具收集玩法（《棋盘活动道具收集玩法 V1.1》）。
           *
           * **id 刻意不叫 `subgame-act-item`**：`isTilePage` 与 App 的渲染分支都按
           * `subgame-` 前缀去 cfg_tile 里找同名格子，叫那个名字会被当成
           * 「菜单里列了、这一版还没配」的玩法格，点进去只有一个空状态。
           *
           * **不带 `versioned`**：整页两半（活动道具管理、活动排期）都是保存即生效、
           * 不进配置版本。挂上版本横幅会得到一个自相矛盾的指路牌——顶上说「此版本已发布，
           * 基于此新建草稿」，而页面两半都标着「保存即生效」、按钮也照常可点。
           * 跟版本走的是 spin 掉落项，那在「spin 奖励配置」页，不在这儿。
           */
          {
            id: "act-item", label: "活动道具", done: true,
            docUrl: "https://portwind.jp.larksuite.com/wiki/QZUbwxA6DiaRG3kOhSEjhQcCpTb",
          },
        ],
      },
      // 多人匹配棋盘格位表（《Fast Ludo 棋盘格位表 v4.0》）：只在玩法体系 = 多人匹配时露出
      { id: "board-cells", label: "棋盘格配置", done: true, versioned: true, mode: "match", docUrl: "https://portwind.jp.larksuite.com/wiki/L4cYwFG0aiLzoRkvQT8j2XelpUb" },
      { id: "lamp-drops", label: "神灯掉落表", done: true, versioned: true, mode: "match", docUrl: "https://portwind.jp.larksuite.com/wiki/Jqv4wzguqifYWpk0nmHjMPfXphd" },
    ],
  },
  {
    id: "card",
    label: "卡牌配置",
    mode: "solo",
    items: [
      { id: "card-item", label: "物品卡", done: true, versioned: true },
      // 按玩家等级配物品包掉落概率（cfg_card_pack.drops 的按等级视图）；卡包抽屉里的掉落改只读，以这页为唯一编辑入口
      { id: "card-drop", label: "物品掉落配置", done: true, versioned: true },
      { id: "card-idol", label: "idol 卡", done: true, versioned: true },
    ],
  },
  {
    id: "idol",
    label: "idol 中心",
    // 2026-11-04 用户定：初始化配置选了「单人主线」的代理不显示 idol 中心 / Idol Ludo任务成就 / 二合任务成就管理 / 合成与订单，只给多人匹配的代理
    mode: "match",
    items: [
      /*
       * **刻意不带 versioned。** idol 名册不受配置版本管辖（idol 方案 §0.1）：
       * 加了会挂一条「当前草稿 vXX」的横幅，误导策划以为要发布才生效；
       * 也会让 wiring.test 要求它进版本横幅集合。
       */
      { id: "idol-list", label: "idol 列表", done: true },
      // 亲密度是数值配置、进配置版本（与 idol 名册相反：名册即时生效，不走版本）
      // 2026-10-23 先撤下菜单：只有老写真拼图会加亲密度、客户端已不走那条路，配了不生效（用户定）
      { id: "idol-intimacy", label: "亲密度配置", done: true, versioned: true, offMenu: true, docUrl: "https://portwind.jp.larksuite.com/wiki/S8jOwQH7Oiolk0k4YNQjoOGopxe" },
      // VIP 照片 / 视频进配置版本（cfg_idol_vip_photo / cfg_idol_vip_video），草稿 → 发布才生效
      { id: "idol-vip-photos", label: "VIP 照片", done: true, versioned: true },
      { id: "idol-vip-videos", label: "VIP 视频", done: true, versioned: true },
    ],
  },
  {
    id: "puzzle",
    label: "拼图配置",
    mode: "solo",
    items: [
      // 2026-10-23 拼图列表 / 价目表先撤下菜单（用户定）：老写真拼图 cfg_puzzle，客户端已改走关卡图片。页面与接口照留
      // 拼图行与价目表都在 cfg_version 里，两页都要 versioned——
      // 漏了就是已发布版本上一排灰输入框，没人告诉策划为什么不能编
      { id: "puzzle-list", label: "拼图列表", done: true, versioned: true, offMenu: true },
      { id: "puzzle-price", label: "拼图价目表", done: true, versioned: true, offMenu: true },
      // 关卡多图（2026-10-21 host 定单独菜单）：一关一行，点进去编辑第 2 张起的图；编辑的是关卡相册块，进配置版本
      { id: "stage-images", label: "关卡图片", done: true, versioned: true },
      // 关卡相册挂在拼图（关卡）下，第 1 张是拼图完成照（服务端合成），进配置版本
      { id: "idol-photos", label: "关卡相册", done: true, versioned: true },
    ],
  },
  {
    id: "pet",
    label: "喵汪角色",
    mode: "solo",
    items: [
      // 进配置版本（src/db/schema/pet.ts 文件头写了为什么它进、idol 不进），
      // 所以必须 versioned——漏了就是已发布版本上一排灰输入框，没人告诉策划为什么不能编
      { id: "pet-list", label: "角色列表", done: true, versioned: true },
      // 升星档：进配置版本，同角色列表。升星功能本身是 V2，这一版只配不用——
      // 配了会下发给客户端画「升星功能即将开放」的预告，所以表要先有
      { id: "pet-star", label: "升星档", done: true, versioned: true, docUrl: "https://portwind.jp.larksuite.com/wiki/J6YWwE1OdiJpZIkSLOQjTX4pp1d" },
      // 领养池不在这里：2026-09-11 起按领养点拆成两份，各自挂在自己的副玩法格页
      // （棋盘配置 → 副玩法配置 → 收养馆 / 草丛）。运营调爆率前总要先看那个点的
      // 出现节奏（保底 / 冷却），两项在同一页上才比得了。
    ],
  },
  {
    id: "act",
    label: "活动配置",
    mode: "solo",
    items: [
      { id: "newbie-activity", label: "新手七日", done: true },
      { id: "auction", label: "竞拍配置", done: true, versioned: true },
      // 每日奖励（登录签到）：周期档 + 累计档都在 cfg_version 里，要 versioned
      { id: "daily-reward", label: "每日奖励", done: true, versioned: true },
      // 存钱罐（2026-10-15）：保存即生效、不进版本；保存时连带写这一期的内购商品，商店商品 ID 在「内购配置」填
      { id: "piggy", label: "存钱罐", done: true },
      // 付费链（2026-10-30，禅道 9009）：同存钱罐保存即生效、不进版本；付费格的商品从「内购配置」里选
      { id: "pay-chain", label: "付费链", done: true },
      // 个人中心的两张社交表也在 cfg_version 里，两页都要 versioned。
      // 事件类型那页同时是 POST /v1/social/events 的上报白名单——改它等于改接口能收什么，
      // 所以它必须跟着版本走（发布才生效），不能像商城那样即时生效
      { id: "social-channel", label: "加入我们", done: true, versioned: true },
      { id: "social-event-type", label: "时间线事件", done: true, versioned: true },
      // 头像框名册也在 cfg_version 里（资源块 frame:all 按版本物化）
      { id: "frame", label: "头像框", done: true, versioned: true },
      // 活表不进配置版本：上下架要即时生效（同 idol）
      { id: "shop-sections", label: "商城分区", done: true },
      { id: "shop", label: "商城商品", done: true },
      // 物品资料：商城礼包里奖励物品的详情（图标、名称、介绍、获取途径）。活表即时生效，同商城；C 端走资源块 asset:info
      { id: "asset-info", label: "物品资料", done: true },
      // 内购：各平台 App + 现金商品的商店商品 ID。活表即时生效，同商城
      { id: "iap", label: "内购配置", done: true },
      // 其他活动 / 商店 / idol 中心 / 拼图：MVP 砍掉（leo 2026-08-21），需求定了再回来
    ],
  },
  // 右半边按「读写对象」分（leo 2026-08-26）：按玩家查/动的归玩家管理，全量聚合的归数据报表，
  // 不碰业务数据的归工具。以后加页面按这条判，不再往「报表」里塞运维视图
  {
    // 合成与订单：八张画板共用一个配置块（整块读写），都进配置版本。设计稿是 Claude Design 的「合成与订单管理端」画布，
    // 不在管端设计项目里，design-ref/exemptions.json 登记了豁免
    id: "g-merge",
    label: "合成与订单",
    mode: "match",
    items: [
      { id: "merge-item", label: "物品", done: true, versioned: true },
      { id: "merge-generator", label: "生成器", done: true, versioned: true },
      { id: "merge-board", label: "棋盘", done: true, versioned: true },
      { id: "merge-order", label: "订单配置", done: true, versioned: true },
      { id: "merge-energy", label: "体力与倍率", done: true, versioned: true },
      { id: "merge-warehouse", label: "仓库", done: true, versioned: true },
      { id: "merge-dex", label: "图鉴", done: true, versioned: true },
      { id: "merge-intimacy-reward", label: "亲密度物品奖励", done: true, versioned: true },
      // 运营删除：不进配置版本（运营动作，发出就生效、不能撤回），只给超管
      { id: "merge-removal", label: "物品删除", done: true, superAdmin: true },
    ],
  },
  {
    id: "g-player",
    label: "玩家管理",
    items: [
      { id: "report-players", label: "玩家面板", done: true },
      /** 充值记录：现金内购单 pay_order 的只读查询；所有管端账号可见，看详情记审计（老大 2026-10-06） */
      { id: "recharge-records", label: "充值记录", done: true },
      /*
       * 系统邮件放这一组，不放「活动配置」，两个理由：
       *
       * 1. **活动配置整组是 `mode: "solo"`**，多人匹配的代理根本看不见它。
       *    邮件与玩法体系无关——跑多人的代理照样要发补偿、发公告。
       * 2. 右半边的分法是「按玩家查/动的归玩家管理」（leo 2026-08-26 的规则，
       *    见本文件上面那条注释）。发邮件是对玩家做事（还会发资产），不是配游戏内容。
       *
       * 活表：发送即生效，没有草稿/发布这一步，所以不带 versioned（docs/sys-mail-plan.md）。
       */
      { id: "mail", label: "系统邮件", done: true },
      /** 举报管理：玩家在名片上举报别人，运营在这里看与标记；活表、不进版本、不做处罚（John 2026-10-05） */
      { id: "reports", label: "举报管理", done: true },
      { id: "report-client-state", label: "客户端数据", done: true, docUrl: "https://portwind.jp.larksuite.com/wiki/U7ucwYObXibZoHkQtDfjLpZypuX" },
    ],
  },
  /*
   * 推送管理（leo 2026-10-20）：离线推送的运营开关，以后推送相关的页面都放这组。
   * 推送事件：服务端登记表 push/events.ts 的每种推送按代理开关；活表、不进配置版本，拨了即时生效。
   */
  {
    id: "g-push",
    label: "推送管理",
    items: [
      { id: "push-events", label: "推送事件", done: true },
    ],
  },
  // 任务 / 成就（《任务与成就配置表 V1.0》）。三个子页各管一块（用户 2026-09-10），
  // 同 萌宠小镇 → 小镇参数 / 主建筑星级表 / 副建筑槽位 的摆法。
  // **刻意不带 versioned**：暂不走配置版本（用户 2026-09-07），保存即生效，同 idol / 商城——
  // 带了会挂「当前草稿 vXX」横幅，误导策划以为要发布才生效
  {
    id: "g-achievement",
    label: "Idol Ludo任务成就",
    mode: "match",
    items: [
      { id: "achievement-tasks", label: "任务管理", done: true },
      { id: "achievement-achievements", label: "成就管理", done: true },
      { id: "achievement-rewards", label: "奖励配置管理", done: true },
    ],
  },
  // 二合任务成就（2026-10-29，口径《idol二合-任务与成就配置.xlsx》）：与 Idol Ludo 的任务成就**完全分开**——自己的表（cfg_erhe_*）、
  // 自己的接口（/admin/api/erhe/*、客户端 /v1/game/erhe/*）、自己的页面（pages/ErheTaskPage.tsx）。前四个子页对应配置表的四页，
  // 标签管理（2026-10-30）配客户端弹窗底部标签的名称 / 顺序 / 启用。
  // 10-26 那版「照 Idol Ludo 复制一份」的 erhe-achievement-* 页已从左栏撤下（服务端 ?game=erhe 的接口留着，没人用）
  {
    id: "g-achievement-erhe",
    label: "二合任务成就管理",
    mode: "match",
    items: [
      { id: "erhe-daily", label: "每日任务", done: true },
      { id: "erhe-chest", label: "每日宝箱", done: true },
      { id: "erhe-weekly", label: "每周任务", done: true },
      { id: "erhe-achievement", label: "成就", done: true },
      { id: "erhe-tabs", label: "标签管理", done: true },
    ],
  },
  {
    id: "g-report",
    label: "数据报表",
    items: [
      { id: "report-spin", label: "spin 落点报表", done: true, docUrl: "https://portwind.jp.larksuite.com/wiki/AqXwwQ3HNiBbQkkzU8AjI1nLpCh" },
      // 对局监控：队列与房间只活在 Durable Object 里、库里只有打完的局有行，
      // 这一页是唯一能看见「排到一半」「成房了没开成」的地方。只读
      { id: "match-monitor", label: "对局监控", done: true, docUrl: "https://portwind.jp.larksuite.com/wiki/ZlmVwskCfiFEuok7ocCjMG6Lphe" },
    ],
  },
  // 模拟器的逐关浮动区间随配置版本保存，需要版本条；其余工具仍独立于配置版本。
  {
    id: "g-tools", label: "工具",
    items: [
      { id: "sim", label: "模拟器", done: true, versioned: true, docUrl: "https://portwind.jp.larksuite.com/wiki/AqXwwQ3HNiBbQkkzU8AjI1nLpCh" },
      // superAdmin: 只有超管看得见（联调追踪会把请求体、响应体和玩家标识推到群里）。
      // 前端藏菜单只是不打扰运营，**真正的拦截在服务端**——改个 URL 就能进来的东西
      // 不叫权限。
      { id: "debug-trace", label: "联调追踪", done: true, superAdmin: true },
      // 同样是超管专用：它签发的是玩家身份票，不是查询数据
      { id: "dev-token", label: "玩家 token", done: true, superAdmin: true },
      // 同上：它能改余额、能指定掷骰结果，服务端另有 test 环境硬闸
      { id: "dev-panel", label: "测试面板", done: true, superAdmin: true },
      // 决定谁能用 force_code 改自己的掷骰结果，范围该比能改配置的人更窄
      { id: "gm", label: "GM 白名单", done: true, superAdmin: true },
      // 经济口径切换（docs/economy-g-model.md §6）：按代理把金额口径从 B 数切到单掷金币 g（或切回）。
      // 停写、补写、激活都动全代理玩家，只给超管；真正的拦截在服务端（requireSuperAdmin）
      { id: "econ-switch", label: "经济口径切换", done: true, superAdmin: true },
      // 代理（A0）注册表：顶栏下拉与登录校验的数据源。加错/停错一个代理会让整片玩家登不进，所以只给超管
      // 普通账号也能进来新建代理（leo 2026-10-19）；编辑 / 导入 / 同步仍只给超管，页面里按权限藏按钮
      { id: "agents", label: "代理管理", done: true },
      // 热更新包的上传 / 发布 / 下架。上传与草稿谁都能做，发布、下架、最低代码版本页面里按超管放按钮（服务端同样拦）
      { id: "hot-update", label: "热更新发布", done: true },
      // 只有根超管可见：加/删普通超管是提权操作，根超管配在环境变量、界面改不了
      // 管端自有账号（独立登录，不依赖中台）：超管给同事建号、禁用、重置密码。提权（is_super）也在这里，所以只给超管
      { id: "accounts", label: "账号管理", done: true, superAdmin: true },
      // Discord 绑定：每个人给自己绑一次，绑完 notify-hub 会私信推登录链接，所以不是超管专属
      { id: "discord-bind", label: "Discord 绑定", done: true },
    ],
  },
  // 教程按岗位组织；入口不依赖代理、配置版本或玩法体系。
  {
    id: "g-help", label: "教程",
    items: [
      { id: "help-videos", label: "课程中心", done: true },
      { id: "help-learning", label: "我的学习", done: true },
      // 进度排名读服务端的进度表（admin/learning.ts），所有登录的人都能看
      { id: "help-rank", label: "进度排名", done: true },
      { id: "help-operations", label: "运营课程", done: true },
      { id: "help-client", label: "客户端课程", done: true },
      { id: "help-server", label: "服务端课程", done: true },
      { id: "help-design", label: "策划与数值", done: true },
      { id: "help-qa", label: "测试与验收", done: true },
      { id: "help-hello-ludo", label: "Hello ludo 课程", done: true },
    ],
  },
];

/**
 * 显示版本横幅（草稿/只读/基于此新建草稿）的页面集合。
 *
 * 从 versioned 标记派生，**不要再手写一份清单**：App 里曾有一份硬编码的数组，
 * spin 页接回来时忘了同步，结果打开已发布版本时整页只读、又没有任何出路——
 * 策划看到的是一排灰输入框，连「为什么不能编」都没人告诉他。
 */
export const VERSIONED_PAGES: string[] = [];

export const ALL_ITEMS: NavItem[] = NAV.flatMap((g) => g.items.flatMap((i) => [i, ...(i.children ?? [])]));
export const findItem = (id: string) => ALL_ITEMS.find((i) => i.id === id);
for (const i of ALL_ITEMS) if (i.versioned) VERSIONED_PAGES.push(i.id);
/** 单项分组在菜单上不显示二级，直接当一级项渲染 */
export const isSingle = (g: NavGroup) => g.items.length === 1 && g.items[0].label === g.label;

/** 该页的规则文档链接；三级页未单独指定时继承父项（与设计 nav.jsx 的 PAGE_LABEL 同口径） */
export function docUrlOf(id: string): string | undefined {
  for (const g of NAV) {
    for (const it of g.items) {
      if (it.id === id) return it.docUrl;
      const kid = it.children?.find((c) => c.id === id);
      if (kid) return kid.docUrl ?? it.docUrl;
    }
  }
  return undefined;
}

/**
 * 菜单里的玩法格 = 种子表 TILE_NAV + **本版本服务端有而种子没有的**（如 coinrain）。
 *
 * 种子只是前端认识的默认七格；玩法格真正的清单在 cfg_tile 里，随版本走。
 * 只按种子画菜单，服务端新加的格子就没有入口（2026-08-27 金币喷泉就是这样丢的）。
 * 动态项的 label 用格子自己的名字兜底——字典里没有 nav.subgame-xxx 时 SideNav 会退回它。
 */
/**
 * 菜单 = 玩法格补齐 + 按「这个代理配成什么样」过滤。
 *
 * 两个选择器都取**当前正在看的那一版**（草稿也算）：运营要能先在草稿里改完再发布。
 *
 * **没选完之前只留全局配置那一组。** 一个刚建出来的代理，左边挂着通关配置、棋盘、
 * 卡牌、拼图……运营不知道该从哪儿下手，也不知道其中哪几组最后根本用不上。
 * 先逼着把初始化那一页选完，选完菜单自己长出来——这是「一步一步配」，
 * 不是把所有入口一次性摊开。
 */
export function navWithTiles(tiles: Tile[], sel: { buildingSystem?: string; gameMode?: string } = {}): NavGroup[] {
  const extra: NavItem[] = tiles
    .filter((t) => !TILE_NAV.some((x) => x.id === `subgame-${t.key}`) && t.key !== ACT_ITEM_TILE)
    .map((t) => ({ id: `subgame-${t.key}`, label: t.name || t.ja || t.key, done: true, versioned: true }));
  const withTiles = !extra.length ? NAV : NAV.map((g) => (g.id !== "board" ? g : {
    ...g,
    items: g.items.map((it) => (it.id !== "subgame" ? it : { ...it, children: [...(it.children ?? []), ...extra] })),
  }));

  const system = sel.buildingSystem === "levels" || sel.buildingSystem === "pettown" ? sel.buildingSystem : "";
  const mode = sel.gameMode === "solo" || sel.gameMode === "match" ? sel.gameMode : "";
  return filterBySelectors(withTiles.map((g) => (g.items.some((it) => it.offMenu) ? { ...g, items: g.items.filter((it) => !it.offMenu) } : g)), system, mode);
}

function filterBySelectors(withTiles: NavGroup[], system: string, mode: string): NavGroup[] {
  /*
   * 玩法体系没选 = 还没初始化：配置类的组只给全局配置那一组（初始化就在里面）。
   * 跟版本配置无关的组（玩家管理、工具、数据报表……没有一项是 versioned）照常给——
   * 以前一并藏掉，切到新代理那一刻连「代理管理」这一页自己都从菜单里消失了（10-10 从零建代理实测）
   */
  const versionedGroup = (g: NavGroup) => g.items.some((it) => it.versioned || (it.children ?? []).some((c) => c.versioned));
  if (!mode) return withTiles
    // 工具组混有版本配置与运维入口；未初始化时只隐藏模拟器，代理管理等仍可用。
    .map((g) => g.id === "g-tools" ? { ...g, items: g.items.filter((it) => !it.versioned) } : g)
    .filter((g) => g.id === "global" || !versionedGroup(g));
  return withTiles.filter((g) => {
    // 单人有的东西多人都要有（leo 2026-09-09）：mode: "solo" 的组在两种体系下都显示，
    // 只有 mode: "match" 那一组是多人独有的。之前按「相等才显示」把 6 个组藏掉了
    if (g.mode === "match" && mode !== "match") return false;
    // 体系没选时两套都留着：这时候运营正要去选，藏起来反而看不到有哪两套
    if (g.system && system && g.system !== system) return false;
    return true;
  }).map((g) => (g.items.some((it) => it.mode === "match")
    // 项级过滤同一条规则：只藏 mode: "match" 的项（棋盘格位表）
    ? { ...g, items: g.items.filter((it) => !(it.mode === "match" && mode !== "match")) }
    : g));
}

/** 超管 / 根超管专用的项：菜单与搜索用同一条判断，免得搜索把普通账号带进超管页 */
export const itemVisible = (it: NavItem, who: { isSuperAdmin?: boolean; isRoot?: boolean }) =>
  (!it.superAdmin || !!who.isSuperAdmin) && (!it.rootOnly || !!who.isRoot);

/** 当前代理的菜单里为什么没有这一页（搜索里给运营解释用） */
export type HiddenReason = "init" | "pettown" | "levels" | "match";

/**
 * 全量菜单里有、`navWithTiles` 按选择器过滤掉的页，及原因。超管页不在其中（不对普通账号暴露）。
 * 规则与 navWithTiles 一一对应：没选玩法体系 → init；组或项是多人专属 → match；组属于另一套建筑体系 → 那一套。
 */
export function hiddenItems(sel: { buildingSystem?: string; gameMode?: string } = {}): { item: NavItem; group: NavGroup; reason: HiddenReason }[] {
  const shown = new Set(navWithTiles([], sel).flatMap((g) => g.items.flatMap((i) => [i.id, ...(i.children ?? []).map((c) => c.id)])));
  const mode = sel.gameMode === "solo" || sel.gameMode === "match" ? sel.gameMode : "";
  const out: { item: NavItem; group: NavGroup; reason: HiddenReason }[] = [];
  for (const g of NAV) {
    for (const it of g.items) {
      if (it.superAdmin || it.offMenu || shown.has(it.id)) continue;
      const reason: HiddenReason = !mode ? "init"
        : g.mode === "match" || it.mode === "match" ? "match"
        : g.system === "pettown" ? "pettown" : "levels";
      out.push({ item: it, group: g, reason });
      for (const c of it.children ?? []) if (!shown.has(c.id)) out.push({ item: c, group: g, reason });
    }
  }
  return out;
}

/** 玩法格页一律受版本管辖——包括只在服务端存在、不在 VERSIONED_PAGES 静态表里的格子 */
export const isTilePage = (page: string) => page.startsWith("subgame-");

export function breadcrumb(id: string): string[] {
  for (const g of NAV) {
    for (const it of g.items) {
      if (it.id === id) return isSingle(g) ? [it.label] : [g.label, it.label];
      const kid = it.children?.find((c) => c.id === id);
      if (kid) return [g.label, it.label, kid.label];
    }
  }
  return [id];
}
